import React, { useEffect, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { ScanTextInput } from '../../../../common/inputs';
import { docInterpretationService } from '../../../../../services/docInterpretationService';
import type {
  DocInterpretationItemHit,
  DocInterpretationLine,
  DocInterpretationOrderHit,
  DocInterpretationResult,
} from '../../../../../types/docInterpretation';
import { effectiveQuantity } from './docInterpretationHelpers';

interface NewOrderLinePanelProps {
  result: DocInterpretationResult;
  line: DocInterpretationLine;
  run: (action: () => Promise<DocInterpretationResult>) => Promise<void>;
  busy: boolean;
}

const fmt = (value: number | null | undefined) =>
  value == null ? '–' : Number(value).toLocaleString('nb-NO', { maximumFractionDigits: 4 });

const orderLabel = (o: DocInterpretationOrderHit) =>
  o.orderNoERP && o.orderNoERP !== String(o.orderNo) ? `${o.orderNoERP} (${o.orderNo})` : String(o.orderNo);

/**
 * «Opprett ny bestillingslinje» (punkt 18, avklart 16.10.2026): innkjøper velger bestilling — standard er bestillingen
 * dokumentet behandles mot, ellers en annen åpen bestilling fra leverandøren — og varen. Linjen opprettes ved OK.
 */
export const NewOrderLinePanel: React.FC<NewOrderLinePanelProps> = ({ result, line, run, busy }) => {
  const { t } = useTranslation();
  const p = 'manager.modules.workingOrders.docInterpretation.detail';
  const id = result.id!;

  const defaultOrderNo = line.orderNo ?? result.orderNos[0] ?? null;
  const [orders, setOrders] = useState<DocInterpretationOrderHit[]>([]);
  const [orderNo, setOrderNo] = useState<number | null>(defaultOrderNo);
  const [orderQuery, setOrderQuery] = useState('');
  const [item, setItem] = useState<{ itemNo: string; description: string | null } | null>(null);
  const [itemQuery, setItemQuery] = useState('');
  const [itemHits, setItemHits] = useState<DocInterpretationItemHit[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setOrderNo(line.orderNo ?? result.orderNos[0] ?? null);
    setItem(line.itemNo ? { itemNo: line.itemNo, description: line.itemDescription ?? line.docDescription } : null);
    setItemQuery(line.docDescription ?? line.docSupplierItemNo ?? '');
    setItemHits(null);
    setError(null);
    // Leverandørens åpne bestillinger; den dokumentet behandles mot er forhåndsvalgt
    docInterpretationService.searchOrders(id, '').then(hits => {
      setOrders(hits);
      // Står bestillingen linjen viser til i dokumentet blant de åpne, er den standard
      if (line.orderNo == null && line.docOrderNo) {
        const ref = line.docOrderNo.replace(/^\D+/, '');
        const match = hits.find(o => o.orderNoERP === line.docOrderNo || o.orderNoERP === ref || String(o.orderNo) === ref);
        if (match) setOrderNo(match.orderNo);
      }
    }).catch(err => setError(err.message));
  }, [id, line, result.orderNos]);

  const searchOrders = async () => {
    setError(null);
    try {
      const hits = await docInterpretationService.searchOrders(id, orderQuery.trim());
      setOrders(hits);
      if (hits.length > 0 && !hits.some(o => o.orderNo === orderNo)) setOrderNo(hits[0].orderNo);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const searchItems = async () => {
    if (!itemQuery.trim()) return;
    setError(null);
    try {
      setItemHits(await docInterpretationService.searchItems(id, itemQuery.trim()));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const button = (primary: boolean) =>
    `px-3 py-2 text-sm rounded-lg border ${primary ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'} disabled:opacity-50`;

  return (
    <div className="p-3 border border-gray-200 rounded-lg space-y-3">
      <div>
        <div className="font-semibold text-gray-800">{t(`${p}.newOrderLineTitle`, {}, 'Opprett ny bestillingslinje')}</div>
        <p className="text-xs text-gray-500">
          {t(`${p}.newOrderLineHelp`, {}, 'Linjen legges på bestillingen ved OK, med antallet fra dokumentet.')}
        </p>
      </div>

      {/* Bestilling: den som behandles er standard; søk etter en annen */}
      <div className="space-y-1">
        <div className="text-gray-500">{t(`${p}.newOrderLineOrder`, {}, 'Bestilling')}</div>
        <div className="flex flex-wrap gap-2">
          <select value={orderNo ?? ''} onChange={e => setOrderNo(e.target.value ? Number(e.target.value) : null)}
            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg bg-white">
            {orderNo != null && !orders.some(o => o.orderNo === orderNo) && <option value={orderNo}>{orderNo}</option>}
            {orders.map(o => (
              <option key={o.orderNo} value={o.orderNo}>{orderLabel(o)}{o.supplierName ? ` · ${o.supplierName}` : ''}</option>
            ))}
          </select>
          <ScanTextInput value={orderQuery} onChange={e => setOrderQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') searchOrders(); }}
            placeholder={t(`${p}.newOrderLineOrderSearch`, {}, 'Søk annen bestilling')}
            className="w-44 px-3 py-2 border border-gray-300 rounded-lg" />
          <button type="button" onClick={searchOrders} disabled={busy} className={button(false)}
            aria-label={t(`${p}.newOrderLineOrderSearch`, {}, 'Søk annen bestilling')}>
            <Search size={16} />
          </button>
        </div>
        {orders.length === 0 && (
          <div className="text-xs text-gray-500">{t(`${p}.newOrderLineNoOrders`, {}, 'Ingen åpne bestillinger fra leverandøren.')}</div>
        )}
      </div>

      {/* Vare: forhåndsvalgt når Compass kjenner varen, ellers søk */}
      <div className="space-y-1">
        <div className="text-gray-500">{t(`${p}.newOrderLineItem`, {}, 'Vare')}</div>
        {item ? (
          <div className="flex items-center justify-between gap-2">
            <div className="font-medium text-gray-800">{item.itemNo}{item.description ? ` · ${item.description}` : ''}</div>
            <button type="button" disabled={busy} className={button(false)} onClick={() => setItem(null)}>
              {t(`${p}.newOrderLineChangeItem`, {}, 'Bytt vare')}
            </button>
          </div>
        ) : (
          <>
            <div className="flex gap-2">
              <ScanTextInput value={itemQuery} onChange={e => setItemQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') searchItems(); }}
                placeholder={t(`${p}.newOrderLineItemSearch`, {}, 'Søk vare: varenr., beskrivelse eller EAN')}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg" />
              <button type="button" onClick={searchItems} disabled={busy} className={button(false)}
                aria-label={t(`${p}.newOrderLineItemSearch`, {}, 'Søk vare: varenr., beskrivelse eller EAN')}>
                <Search size={16} />
              </button>
            </div>
            {itemHits && itemHits.length === 0 && (
              <div className="text-gray-500">{t(`${p}.newOrderLineNoItems`, {}, 'Ingen varer passer.')}</div>
            )}
            {itemHits && itemHits.length > 0 && (
              <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg max-h-56 overflow-auto bg-white">
                {itemHits.map(hit => (
                  <li key={hit.itemNo} className="flex items-center justify-between gap-2 px-3 py-2">
                    <div>
                      <div className="font-medium text-gray-800">{hit.itemNo}</div>
                      <div className="text-xs text-gray-500">{hit.description}{hit.itemBarCode ? ` · ${hit.itemBarCode}` : ''}</div>
                    </div>
                    <button type="button" className={button(false)}
                      onClick={() => setItem({ itemNo: hit.itemNo, description: hit.description })}>
                      {t(`${p}.newOrderLineChoose`, {}, 'Velg')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <div className="text-gray-700">
        {t(`${p}.newOrderLineQuantity`, { '0': fmt(effectiveQuantity(line)), '1': line.docUnit ?? '' },
          `Antall: ${fmt(effectiveQuantity(line))} ${line.docUnit ?? ''}`)}
      </div>

      {error && <div className="text-red-800">{error}</div>}

      <button type="button" disabled={busy || orderNo == null || item == null} className={button(true)}
        onClick={() => run(() => docInterpretationService.createOrderLine(id, line.id, orderNo!, item!.itemNo))}>
        <Plus size={14} className="inline mr-1" />{t(`${p}.newOrderLineCreate`, {}, 'Opprett ved OK')}
      </button>
    </div>
  );
};
